import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useReconnectingSocket } from "@/hooks/useReconnectingSocket";
import { useLiveData } from "@/context/LiveDataContext";
import { Gauge, Field, Dot } from "@/components/common";
import CommandCenter from "@/components/CommandCenter";
import FlightControllerPanel from "@/components/FlightControllerPanel";
import TelemetryCharts from "@/components/TelemetryCharts";
import MissionStatusCard from "@/components/MissionStatusCard";
import BaseConfigCard from "@/components/BaseConfigCard";
import { num, batteryColor, batteryText, positionText, statusColor, timeAgo, fmtTime, gpsLabel, gpsColor, telemetryLabel, telemetryColor } from "@/lib/format";
import { X, Activity, Gamepad2, Stethoscope } from "lucide-react";
import DroneDiagnosticModal from "@/components/DroneDiagnosticModal";
import { NetworkStatusWidget } from "@/components/NetworkStatusWidget";

const DRONE_IMG = "https://images.unsplash.com/photo-1656011196400-2c548b70a981";

export default function DroneDetailPanel({ droneId, base, onClose, onRefresh }) {
  const { events } = useLiveData();
  const navigate = useNavigate();
  const [tele, setTele] = useState(base || {});
  const [droneWs, setDroneWs] = useState(base?.drone_ws_connected);
  const [lastAck, setLastAck] = useState(null);
  const [liveConnected, setLiveConnected] = useState(false);
  const [history, setHistory] = useState([]);
  const [range, setRange] = useState("live");
  const [loadingHist, setLoadingHist] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const tickRef = useRef(0);

  useEffect(() => { setTele((t) => ({ ...t, ...base })); }, [base]);

  const capFor = (r) => (r === "live" ? 120 : 1000);

  const pushHistory = useCallback((d) => {
    setHistory((h) => {
      const point = {
        t: tickRef.current++,
        battery: d.battery == null || !Number.isFinite(Number(d.battery)) ? null : Number(d.battery),
        alt: Number(d.relative_alt) || 0,
        speed: Number(d.speed) || 0,
        signal: Number(d.signal) || 0,
      };
      return [...h, point].slice(-capFor(range));
    });
  }, [range]);

  // Load persisted history when drone or range changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (range === "live") { setHistory([]); tickRef.current = 0; return; }
      setLoadingHist(true);
      const hours = { "1h": 1, "6h": 6, "24h": 24 }[range] || 1;
      const start = new Date(Date.now() - hours * 3600 * 1000).toISOString();
      try {
        const { data } = await api.get(`/drones/${droneId}/telemetry-history`, {
          params: { start_date: start, max_points: 300 },
        });
        if (cancelled) return;
        const pts = (data.points || []).map((p, i) => ({
          t: i,
          battery: p.battery == null || !Number.isFinite(Number(p.battery)) ? null : Number(p.battery),
          alt: Number(p.alt) || 0,
          speed: Number(p.speed) || 0,
          signal: Number(p.signal) || 0,
        }));
        tickRef.current = pts.length;
        setHistory(pts);
      } catch (e) { if (!cancelled) setHistory([]); }
      finally { if (!cancelled) setLoadingHist(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [droneId, range]);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get(`/drones/${droneId}/status`);
      setTele((t) => ({ ...t, ...data }));
      setDroneWs(data.drone_ws_connected);
    } catch (e) { /* ignore */ }
  }, [droneId]);

  useEffect(() => { fetchStatus(); const t = setInterval(fetchStatus, 3000); return () => clearInterval(t); }, [fetchStatus]);

  const onMessage = useCallback((msg) => {
    const { type, data } = msg;
    if (type === "telemetry" || type === "telemetry_snapshot") {
      if (data) { setTele((t) => ({ ...t, ...data })); pushHistory(data); }
    } else if (type === "command_ack") {
      setLastAck(data);
    } else if (type === "drone_ws_connected") setDroneWs(true);
    else if (type === "drone_ws_disconnected") setDroneWs(false);
  }, [pushHistory]);

  useReconnectingSocket({ path: `/api/ws/telemetry/${droneId}`, onMessage, onStatus: setLiveConnected });

  const d = tele;
  const sc = statusColor(d);
  const droneEvents = events.filter(
    (e) => e.drone_id === droneId && (e.type === "command_ack" || e.type === "command_queued" || e.type === "mission_created")
  ).slice(0, 8);

  return (
    <div data-testid="drone-detail-panel" className="h-full flex flex-col bg-[#141414] border-l border-white/10">
      {/* header */}
      <div className="relative h-28 shrink-0 overflow-hidden border-b border-white/10">
        <img src={DRONE_IMG} alt="drone" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] to-transparent" />
        <div className="absolute top-3 right-3">
          {onClose && (
            <button data-testid="close-detail" onClick={onClose} className="w-7 h-7 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="absolute bottom-3 left-4 flex items-center gap-2">
          <Dot color={sc} pulse={d.status === "online"} />
          <div>
            <div className="font-chivo text-xl font-black tracking-tighter">{droneId}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: sc }}>{d.status || "—"}</div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {d.battery != null ? <Gauge value={d.battery} label="Batterie" color={batteryColor(d.battery)} />
          : <Field label="Batterie" value={batteryText(d)} color="#F59E0B" testid={`battery-state-${droneId}`} />}

        <div className="grid grid-cols-2 gap-2">
          <Field label="Altitude (rel.)" value={num(d.relative_alt)} unit="m" />
          <Field label="Vitesse" value={num(d.speed)} unit="m/s" />
          <Field label="Mode" value={d.mode || "—"} />
          <Field label="Signal" value={num(d.signal, 0)} unit="%" />
          <Field label="GPS" value={gpsLabel(d).replace("GPS ", "")} color={gpsColor(d)} testid={`gps-state-${droneId}`} />
          <Field label="Télémétrie" value={telemetryLabel(d).replace("TÉLÉMÉTRIE ", "")} color={telemetryColor(d)} testid={`telemetry-state-${droneId}`} />
          <Field label="Armé" value={d.armed ? "OUI" : "NON"} color={d.armed ? "#F59E0B" : "#A3A3A3"} />
          <Field label="Position" value={positionText(d)} testid={`position-state-${droneId}`} />
          <Field label="MAVLink" value={d.mavlink_connected === false ? "PERDU" : d.mavlink_connected ? "OK" : "—"}
            color={d.mavlink_connected === false ? "#EF4444" : undefined} testid={`mavlink-state-${droneId}`} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Field label="WS Télémétrie" value={liveConnected ? "LIVE" : "OFF"} color={liveConnected ? "#22C55E" : "#525252"} />
          <Field label="Connexion drone" value={droneWs ? "ACTIVE" : "INACTIVE"} color={droneWs ? "#22C55E" : "#525252"} />
        </div>

        <Field label="Dernier signal" value={timeAgo(d.last_seen)} />

        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3">
          <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#525252] mb-1">Dernier ACK commande</div>
          {lastAck ? (
            <div className="font-mono text-xs space-y-0.5" data-testid="last-ack">
              <div><span className="text-[#A3A3A3]">{lastAck.command}</span> · <span style={{ color: lastAck.status === "executed" ? "#22C55E" : "#F59E0B" }}>{lastAck.status}</span></div>
              <div className="text-[#A3A3A3]">{lastAck.message}</div>
            </div>
          ) : <div className="font-mono text-xs text-[#525252]">Aucun ACK récent</div>}
        </div>

        {/* Telemetry history charts */}
        <div data-testid="telemetry-charts">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] mb-2 flex items-center gap-1.5">
            <Activity size={12} /> Historique télémétrie
            <span className="ml-auto flex items-center gap-1">
              {["live", "1h", "6h", "24h"].map((r) => (
                <button key={r} data-testid={`range-${r}`} onClick={() => setRange(r)}
                  className={`px-1.5 py-0.5 rounded-sm text-[9px] tracking-wider uppercase transition-colors ${range === r ? "bg-[#3B82F6] text-white" : "text-[#525252] hover:text-white border border-white/10"}`}>
                  {r}
                </button>
              ))}
            </span>
          </div>
          {loadingHist ? (
            <div className="font-mono text-[11px] text-[#525252] py-6 text-center">Chargement de l'historique...</div>
          ) : (
            <TelemetryCharts history={history} />
          )}
          {!loadingHist && history.length === 0 && (
            <div className="font-mono text-[10px] text-[#525252] mt-1">
              {range === "live" ? "En attente de télémétrie live..." : "Aucune donnée sur cette période"}
            </div>
          )}
        </div>

        {/* Command / ACK events for this drone */}
        <div data-testid="drone-events">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] mb-2">Événements commandes / ACK</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {droneEvents.length === 0 && <div className="font-mono text-[11px] text-[#525252]">Aucun événement récent</div>}
            {droneEvents.map((e, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-1.5 font-mono text-[10px]">
                <span className="text-[#525252]">{fmtTime(e.ts || e.created_at)}</span>
                <span style={{ color: e.type === "command_ack" ? "#22C55E" : "#3B82F6" }}>{e.command || e.type}</span>
                <span className="text-[#A3A3A3] truncate flex-1" title={e.message}>{e.message}</span>
                {e.status && <span className="text-[#A3A3A3] uppercase">{e.status}</span>}
              </div>
            ))}
          </div>
        </div>

        <MissionStatusCard droneId={droneId} onChanged={onRefresh} />

        <NetworkStatusWidget droneId={droneId} variant="panel" interval={12000} />

        <button data-testid="open-diagnostic" onClick={() => setShowDiag(true)}
          className="w-full flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-wider bg-[#22C55E]/15 border border-[#22C55E]/40 text-[#22C55E] rounded-sm py-2.5 hover:bg-[#22C55E]/25 transition-colors">
          <Stethoscope size={15} /> Lancer un diagnostic
        </button>

        <button data-testid="open-manual-control" onClick={() => navigate(`/manual-control/${droneId}`)}
          className="w-full flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-wider bg-[#3B82F6]/15 border border-[#3B82F6]/40 text-[#3B82F6] rounded-sm py-2.5 hover:bg-[#3B82F6]/25 transition-colors">
          <Gamepad2 size={15} /> Ouvrir pilotage manuel
        </button>

        <FlightControllerPanel fc={d?.flight_controller} />
        <CommandCenter drone={{ ...d, drone_id: droneId }} droneId={droneId} onSent={onRefresh} />

        <BaseConfigCard droneId={droneId} onChanged={onRefresh} />
      </div>
      {showDiag && <DroneDiagnosticModal droneId={droneId} onClose={() => setShowDiag(false)} />}
    </div>
  );
}
